import { Influencer, Post, SocialAccount } from "types/influencer"
import { BasicSocialPost, SocialPost } from "../collaborations"

export interface CampaignApplications {
    total: number,
    count: number,
    items: CampaignApplication[],
    campaign: {
        budget: string,
        earning_mode: string
    }
}

export interface DisplayData {
    color: string
}

export interface CampaignApplication {
    uuid: string,
    status: 'pending' | 'accepted' | 'rejected' | 'processing',
    requirement_match: string,
    requirement_match_details: DisplayData,
    authenticity_score: string,
    average_views: string,
    authenticity_score_details: DisplayData,
    requirement_matches?: RequirementMatches,
    influencer?: Influencer,
    bookmarked?: 'y' | 'n'
    language?: {name: string},
    social_account: SocialAccount,
    social_post?: SocialPost,
    answers?: ScreeningQuestionAnswer[],
    evaluation: AIEvaluation
}

export interface AIEvaluation{evaluation: {
    final_verdict: {
        recommendation: "REJECT" | "ACCEPT",
        pitch_statement: string
    },
    logic_gate_audit: {
        saturation_risk: string,
        efficiency_check: string,
        operational_pass: boolean,
        audience_alignment: string
    },
    strategic_analysis: {
        strengths: string[],
        weaknesses: string[],
        friction_report: string
    },
    evaluation_metadata: {
        match_status: string,
        fitness_score: number,
        fitness_score_breakup: {
            strategic_value: number,
            operational_match: number,
            tactical_alignment: number
        },
        precedence_resolution: string
    },
    recommendation: "REJECT" | "ACCEPT",
    match_status: string
}}

export interface ScreeningQuestionAnswer {
    question: string,
    answer: string
}

export interface RequirementMatches {
    [key: string] : {
        name: string,
        type: string,
        match: boolean
    }
}

export type ApplicationsFilterTab = 'applications' | 'shortlisted';

export interface CampaignApplicationSlice {
    loading: boolean,
    revalidating: boolean,
    error: string | null,
    /** Bumped when shortlist membership changes so ShortlistedApps can refetch summary. */
    shortlistedSummaryEpoch: Record<string, number>,
    campaigns: Record<string, {
        total: number,
        totalsByBookmarked?: Partial<Record<'y' | 'n', number>>,
        filtered_lists: Record<string, {
            count: number,
            list: CampaignApplication[],
            page: number,
            has_more: boolean,
            last_fetched_at: number,
            filters: any,
            expired: boolean,
            pendingRevalidateMaxPage?: number,
        }>
        applications: Record<string, {
            application: CampaignApplication,
            last_fetched_at: number
        }>
    }>,
    filtersApplications: ApplicationFiltersNew,
    filtersShortlisted: ApplicationFiltersNew,
    applicationLoading: boolean,
    applicationError: string | null
    applied_filter_id: string | null,
    postsLoading: boolean,
    posts: Record<string, Record<string,{
        items: ApplicationPost[],
        count: number,
        last_fetched_at: number
    }>>
}

export interface ApplicationPost {
    bookmarked: "n" | "y",
    ig_post: Post,
    social_post: SocialPost
}

export enum ScoreLevel {
    LOW = 'low',
    BELOW_AVERAGE = 'below_average',
    AVERAGE = 'average',
    ABOVE_AVERAGE = 'above_average',
    HIGH = 'high'
}


export const ScoreLevelLabel = {
    [ScoreLevel.LOW]: 'Low',
    [ScoreLevel.BELOW_AVERAGE]: 'Below Average',
    [ScoreLevel.AVERAGE]: 'Average',
    [ScoreLevel.ABOVE_AVERAGE]: 'Above Average',
    [ScoreLevel.HIGH]: 'High'
};

export interface ApplicationsFilters  {
    min_requirement_match?: ScoreLevel,
    min_authenticity_score?: ScoreLevel,
    min_engagement_rate?: ScoreLevel | null,
    declined?: 'show' | 'hide',
    page: number,
    sort: SortOptions
}

export enum SortOptions {
    RECOMMENDATION = 'performance_score',
    LATEST = 'latest',
    MATCH_PERCENTAGE = 'requirement_match',
    FOLLOWERS_COUNT = 'followers_count',
    ENGAGEMENT_SCORE = 'engagement_score',
    AVERAGE_VIEWS = 'average_views',
}

export const SortOptionLabel = {
    [SortOptions.MATCH_PERCENTAGE]: 'Match Percentage',
    [SortOptions.FOLLOWERS_COUNT]: 'Followers Count',
    [SortOptions.ENGAGEMENT_SCORE]: 'Engagement Score',
    [SortOptions.AVERAGE_VIEWS]: 'Average Views',
    [SortOptions.LATEST]: "Latest",
    [SortOptions.RECOMMENDATION]: "Recommendation"
}

export interface ApplicationPosts {
    items: ApplicationPost[],
    count:number
}

export interface ApplicationFiltersNew {
    gender?: string;
    follower_count?: string | number,
    niches?: string[];
    cities?: string[] | null;
    regions?: string[] | null;
    age_min?: string|number,
    age_max?: string|number,
    audience_cities?: string[];
    audience_age_groups?: string[];
    languages?: string[];
    audience_gender?: string;
    topics?: string[],
    exclude_topics?: string[],
    min_authenticity_score?: number,
    min_engagement_score?: number,
    declined?: 'show' | 'hide',
    page: number,
    sort: SortOptions,
    q?: string,
    bookmarked?: 'y' | 'n',
    [key:string]: any
}

export interface ApplicationCost {
    cost: number;
    breakup: {
        [key: string]: number;
    };
    breakup_items: {
        name: string,
        value: number
    }[];
    wallet_balance: number;
}

export interface ApplicationCostSummary {
    breakup_items: {
        name: string,
        value: number
    }[];
    total: number;
    net: number;
    breakup: {
        payout: number;
        commission: number;
    };
    shortfall: number;
    load_amount: number;
}

export interface ShortlistedSummary {
    cost: ApplicationCostSummary;
    application_count: number;
    roi: {
        average_views: number;
        average_engagement: number;
        cpv: number;
        cpe: number;
    };
    creator_tiers: {
        [key: string]: {
            count: number;
            cost: number;
        };
    };
    wallet_balance: number;
}